// pages/Products/ProductList.js (with filter for wholesale products)
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiImage, FiEye, FiFilter, FiPackage, FiGrid } from 'react-icons/fi';
import { productService } from '../../services/productService';
import { categoryService } from '../../services/categoryService';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import ErrorAlert from '../../components/Common/ErrorAlert';
import ConfirmDialog from '../../components/Common/ConfirmDialog';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { toast } from 'react-hot-toast';
import { getImageUrl } from '../../utils/constants';

const ProductList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [filterType, setFilterType] = useState('all'); // 'all', 'wholesale', 'retail'
  const queryClient = useQueryClient();

  // Fetch products
  const { data: products, isLoading: productsLoading, error: productsError } = useQuery(
    'products',
    () => productService.getAll({ limit: 1000 })
  );

  // Fetch categories
  const { data: categories, isLoading: categoriesLoading } = useQuery(
    'categories',
    categoryService.getAll
  );

  const isLoading = productsLoading || categoriesLoading;

  // Debug: Log product data when loaded
  useEffect(() => {
    if (products && products.length > 0) {
      console.log('=== PRODUCT DEBUG ===');
      console.log('First product:', products[0]);
      console.log('First product images:', products[0].images);
      if (products[0].images && products[0].images.length > 0) {
        console.log('First image fullUrl:', products[0].images[0].fullUrl);
        console.log('First image url:', products[0].images[0].url);
        console.log('First image filepath:', products[0].images[0].filepath);
      }
    }
  }, [products]);

  const deleteMutation = useMutation(
    (id) => productService.delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('products');
        toast.success('លុបផលិតផលដោយជោគជ័យ');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'មិនអាចលុបផលិតផលបានទេ');
      },
    }
  );

  const handleDelete = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
      setDeleteId(null);
    }
  };

  const getCategoryName = (categoryId) => {
    if (!categories) return 'គ្មានប្រភេទ';
    const category = categories.find(cat => cat.id === categoryId);
    return category?.name || 'គ្មានប្រភេទ';
  };

  // Check if product is wholesale (contains "បោះដុំ")
  const isWholesaleProduct = (product) => {
    return product.title && product.title.includes('បោះដុំ');
  };

  // Filter products based on search and wholesale filter
  const getFilteredProducts = () => {
    if (!products) return [];

    let filtered = products;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply wholesale/retail filter
    if (filterType === 'wholesale') {
      filtered = filtered.filter(product => isWholesaleProduct(product));
    } else if (filterType === 'retail') {
      filtered = filtered.filter(product => !isWholesaleProduct(product));
    }

    return filtered;
  };

  const filteredProducts = getFilteredProducts();

  // Count statistics
  const wholesaleCount = products?.filter(p => isWholesaleProduct(p)).length || 0;
  const retailCount = products?.filter(p => !isWholesaleProduct(p)).length || 0;
  const totalCount = products?.length || 0;

  if (isLoading) return <LoadingSpinner />;
  if (productsError) return <ErrorAlert message="មិនអាចផ្ទុកផលិតផលបានទេ" />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">ផលិតផល</h1>
        <Link
          to="/admin/products/new"
          className="bg-red-600 text-white px-4 py-2 text-sm font-medium flex items-center hover:bg-red-700 transition-colors rounded"
        >
          <FiPlus className="mr-2" /> បន្ថែមផលិតផល
        </Link>
      </div>

      {/* Filter Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-4" aria-label="Tabs">
          <button
            onClick={() => setFilterType('all')}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
              filterType === 'all'
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <FiGrid size={16} />
              <span>ទាំងអស់</span>
              <span className="ml-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                {totalCount}
              </span>
            </div>
          </button>
          
          <button
            onClick={() => setFilterType('retail')}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
              filterType === 'retail'
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <FiPackage size={16} />
              <span>លក់រាយ</span>
              <span className="ml-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                {retailCount}
              </span>
            </div>
          </button>
          
          <button
            onClick={() => setFilterType('wholesale')}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
              filterType === 'wholesale'
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <FiFilter size={16} />
              <span>បោះដុំ</span>
              <span className="ml-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                {wholesaleCount}
              </span>
            </div>
          </button>
        </nav>
      </div>

      {/* Search */}
      <div className="relative">
        <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="ស្វែងរកផលិតផល..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-200 focus:outline-none focus:border-red-600 rounded"
        />
      </div>

      {/* Products Table */}
      <div className="bg-white border border-gray-200 overflow-x-auto rounded">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ផលិតផល</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ប្រភេទ</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">តម្លៃ</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">បញ្ចុះតម្លៃ</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">រូបភាព</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">បានបង្កើត</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">សកម្មភាព</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredProducts?.map((product) => {
              const isWholesale = isWholesaleProduct(product);
              return (
                <tr key={product.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {product.images?.[0] && (
                        <>
                          {/* Debug log */}
                          {console.log(`Rendering image for product ${product.id}:`, {
                            hasFullUrl: !!product.images[0].fullUrl,
                            fullUrl: product.images[0].fullUrl,
                            filepath: product.images[0].filepath,
                            usedSrc: product.images[0].fullUrl || getImageUrl(product.images[0].filepath, 'products')
                          })}
                          <img
                            src={product.images[0].fullUrl || getImageUrl(product.images[0].filepath, 'products')}
                            alt={product.title}
                            className="w-10 h-10 object-cover mr-3 rounded"
                            onError={(e) => {
                              console.error('Image failed to load:', e.target.src);
                              e.target.onerror = null;
                              e.target.src = "https://via.placeholder.com/40x40/F3F4F6/9CA3AF?text=No+Image";
                            }}
                          />
                        </>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900">{product.title}</p>
                          {isWholesale && (
                            <span className="px-2 py-0.5 text-xs bg-red-100 text-red-600 rounded-full font-medium">
                              បោះដុំ
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">ID: {product.id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 bg-red-50 text-red-600 text-xs rounded">
                      {getCategoryName(product.category_id)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <p className="font-medium text-gray-900">{formatCurrency(product.price)}</p>
                      {product.discount_percent > 0 && (
                        <p className="text-xs text-red-600">បញ្ចុះ {product.discount_percent}%</p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {product.discount_percent > 0 ? (
                      <span className="text-red-600 font-medium">{product.discount_percent}%</span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <FiImage className="mr-1 text-gray-400" />
                      <span className="text-gray-600">{product.images?.length || 0}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(product.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-2">
                      <Link
                        to={`/admin/products/${product.id}`}
                        className="p-2 text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors rounded"
                        title="មើល"
                      >
                        <FiEye className="w-4 h-4" />
                      </Link>
                      <Link
                        to={`/admin/products/edit/${product.id}`}
                        className="p-2 text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors rounded"
                        title="កែប្រែ"
                      >
                        <FiEdit2 className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => setDeleteId(product.id)}
                        className="p-2 text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors rounded"
                        title="លុប"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filteredProducts?.length === 0 && (
          <div className="text-center py-12">
            {searchTerm ? (
              <>
                <p className="text-gray-500">រកមិនឃើញផលិតផលដែលមានឈ្មោះ "{searchTerm}"</p>
                <button
                  onClick={() => setSearchTerm('')}
                  className="mt-2 text-red-600 hover:text-red-700 text-sm"
                >
                  សម្អាតការស្វែងរក
                </button>
              </>
            ) : filterType === 'wholesale' ? (
              <div className="space-y-2">
                <FiFilter size={48} className="mx-auto text-gray-300" />
                <p className="text-gray-500">មិនមានផលិតផលបោះដុំទេ</p>
                <p className="text-sm text-gray-400">បន្ថែមផលិតផលដែលមានពាក្យ "បោះដុំ" ក្នុងឈ្មោះ</p>
              </div>
            ) : filterType === 'retail' ? (
              <div className="space-y-2">
                <FiPackage size={48} className="mx-auto text-gray-300" />
                <p className="text-gray-500">មិនមានផលិតផលលក់រាយទេ</p>
              </div>
            ) : (
              <div className="space-y-2">
                <FiPackage size={48} className="mx-auto text-gray-300" />
                <p className="text-gray-500">មិនមានផលិតផល</p>
                <Link
                  to="/admin/products/new"
                  className="inline-block mt-2 text-red-600 hover:text-red-700 text-sm"
                >
                  បន្ថែមផលិតផលដំបូង
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Summary Footer */}
        {filteredProducts?.length > 0 && (
          <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-500">
                បង្ហាញ {filteredProducts.length} ក្នុងចំណោម {totalCount} ផលិតផល
              </span>
              {filterType === 'wholesale' && wholesaleCount > 0 && (
                <span className="text-red-600">
                  <FiFilter className="inline mr-1" size={14} />
                  ផលិតផលបោះដុំ {wholesaleCount}
                </span>
              )}
              {filterType === 'retail' && retailCount > 0 && (
                <span className="text-green-600">
                  <FiPackage className="inline mr-1" size={14} />
                  ផលិតផលលក់រាយ {retailCount}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="លុបផលិតផល"
        message="តើអ្នកប្រាកដថាចង់លុបផលិតផលនេះ? សកម្មភាពនេះមិនអាចត្រឡប់វិញបានទេ។"
        confirmText="លុប"
        cancelText="បោះបង់"
      />
    </div>
  );
};

export default ProductList;
